import { z } from "zod";

import type { WaypointIcon } from "@mountayaapp/types/api/api";

/**
 * waypointIconSchema is the full renderer / sprite contract — every icon the
 * generated WaypointIcon enum carries, so a sprite an itinerary tile references
 * (including the auto-emitted flag-start / flag-end endpoint glyphs) is one the
 * sprite generator can always render. Its inferred type is the generated
 * WaypointIcon. The named icons (droplet, camping, hut, bed, …) map a POI
 * category onto a marker — water becomes droplet, an alpine refuge becomes hut.
 * Zod-first because the value is shared with every tool that writes an icon and
 * with the sidenav picker that lists the user-selectable subset.
 */
export const waypointIconSchema = z.enum([
  "circle-warning",
  "megaphone",
  "bed",
  "apartment-building",
  "camper-van",
  "circle-check",
  "parking-sign",
  "medical-cross",
  "droplet",
  "eye",
  "flag",
  "flag-start",
  "flag-end",
  "hut",
  "circle-info",
  "map-pin",
  "camping",
  "trophy",
  "cutlery",
  "bus",
  "train",
  "cable-car",
  "charging-station",
  "gate",
  "toilet",
  "shower",
  "trash",
  "shop",
  "picnic",
  "helicopter",
  "signal",
  "climbing",
  "ladder",
]) satisfies z.ZodType<WaypointIcon>;

/**
 * userWaypointIconSchema is the icon vocabulary a user-supplied waypoint may
 * carry: the renderer contract minus the two endpoint icons (flag-start,
 * flag-end), which are reserved for the start/end markers an itinerary
 * auto-emits at the head and tail of its segment chain. The waypoint create /
 * recolor / icon tools build on this so the bus rejects an attempt to claim an
 * endpoint icon before any write goes out; the backend domain layer applies
 * the same rule as defense in depth. The sidenav icon picker reads its
 * options off this schema, so adding or removing a user-pickable icon is a
 * one-line change here.
 */
export const userWaypointIconSchema = waypointIconSchema.exclude(["flag-start", "flag-end"]);

/**
 * UserWaypointIcon is the icon vocabulary a user-supplied waypoint may carry,
 * derived from userWaypointIconSchema.
 */
export type UserWaypointIcon = z.infer<typeof userWaypointIconSchema>;

/**
 * USER_WAYPOINT_ICONS is the ordered list of icons the sidenav picker grid
 * walks. Held as a tuple so the renderer + the schema agree on the same set,
 * and a new icon added to the user-selectable vocabulary surfaces here at the
 * picker grid in one edit.
 */
export const USER_WAYPOINT_ICONS: readonly UserWaypointIcon[] = userWaypointIconSchema.options;

/**
 * WaypointIconGroupId names one section of the icon picker. The ids are the
 * question the user is answering when they scan that section — where does this
 * point sit on the route, how do you reach it, where do you sleep, what does it
 * offer, what does it warn you about.
 */
export type WaypointIconGroupId = "access" | "facilities" | "markers" | "safety" | "stay";

/**
 * WAYPOINT_ICON_GROUPS partitions the user-pickable vocabulary into the sections
 * the picker renders. A flat grid stopped working once the vocabulary passed
 * roughly twenty glyphs: the user has to recognise a shape rather than look up a
 * meaning, and a single unlabelled block of thirty makes that a scan of the whole
 * set every time.
 *
 * The partition is exhaustive and disjoint over USER_WAYPOINT_ICONS, pinned by a
 * test rather than by the type system — TypeScript cannot express "these arrays
 * union to exactly this set" — so a new icon that lands in no group, or in two,
 * fails the suite instead of quietly disappearing from the picker.
 */
export const WAYPOINT_ICON_GROUPS: ReadonlyArray<{
  icons: readonly UserWaypointIcon[];
  id: WaypointIconGroupId;
}> = [
  {
    icons: ["map-pin", "flag", "circle-info", "circle-check", "megaphone", "trophy", "eye"],
    id: "markers",
  },
  {
    icons: ["parking-sign", "bus", "train", "cable-car", "charging-station", "gate"],
    id: "access",
  },
  { icons: ["bed", "hut", "apartment-building", "camping", "camper-van"], id: "stay" },
  {
    icons: ["droplet", "cutlery", "shop", "toilet", "shower", "picnic", "trash"],
    id: "facilities",
  },
  {
    icons: ["circle-warning", "medical-cross", "helicopter", "signal", "climbing", "ladder"],
    id: "safety",
  },
];
